import { Component, inject, input, OnInit, output } from '@angular/core';
import { AbstractControl, NonNullableFormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// RxJS
import { finalize } from 'rxjs/operators';

// FontAwesome
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

// Services
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../services/auth.service';
import { PasswordService } from '../../services/password.service';

// Components
import { PasswordInputComponent } from "../../../shared/components/password-input/password-input.component";
import { LoadingButtonComponent } from '../../../shared/components/loading-button/loading-button.component';


export const passwordMatchValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');

  if (!password || !confirmPassword) return null;

  if (password.value !== confirmPassword.value) {
    confirmPassword.setErrors({ ...confirmPassword.errors, mismatch: true });
    return { mismatch: true };
  } else {
    if (confirmPassword.hasError('mismatch')) {
      const errors = { ...confirmPassword.errors };
      delete errors['mismatch'];
      if (Object.keys(errors).length === 0) {
        confirmPassword.setErrors(null);
      } else {
        confirmPassword.setErrors(errors);
      }
    }
    return null;
  }
};

@Component({
  selector: 'app-change-password',
  imports: [ReactiveFormsModule, FontAwesomeModule, PasswordInputComponent, LoadingButtonComponent],
  templateUrl: './change-password.component.html'
})
export class ChangePasswordComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly authSrv = inject(AuthService);
  private readonly toastSrv = inject(ToastService);
  private readonly pwdSrv = inject(PasswordService);

  // Se arrivo da un link di set prima password, l'oobCode è presente
  readonly oobCode = input<string>();
  readonly tenantId = input<string>();
  readonly uid = input<string>();

  // Se arrivo dal processo forgot password con OTP
  readonly forgotPassword = input<boolean>(false);

  // Se arrivo dal processo di cambio password per password scaduta
  readonly expiredPassword = input<boolean>(false);

  passwordChanged = output<void>();
  resetOTP = output<void>();

  faLock = faLock;

  loading = false;
  loadingOobVerification = false;
  loadingOobResend = false;

  requiredOldPassword: boolean = true;
  newPasswordMode: 'FirstTime' | 'ExpiredPassword' | 'OTPReset' | 'StandardChange' = 'StandardChange';

  oobCodeErrorMsg = '';
  oobCodeNotValid: 'Deprecated' | 'Resended' | '' = '';

  passwordForm = this.fb.group({
    oldPassword: ['', Validators.required],
    password: ['', [
      Validators.required,
      Validators.minLength(8),
      this.pwdSrv.passwordComplexityValidator()
    ]],
    confirmPassword: ['', Validators.required]
  }, { validators: passwordMatchValidator });

  ngOnInit(): void {
    const oobCode = this.oobCode();
    const tenantId = this.tenantId();
    const uid = this.uid();

    // FistTime: Se l'utente ha un oobCode arriva dal processo di prima password, non deve inserire la vecchia password
    if (oobCode && tenantId && uid) {
      this.verifyOobCode(oobCode, tenantId, uid);
      this.newPasswordMode = 'FirstTime';
      this.removeOldPasswordControl();
    }

    // OTPReset: Se l'utente arriva dal processo di reset password con OTP
    if (this.forgotPassword()) {
      this.newPasswordMode = 'OTPReset';
      this.removeOldPasswordControl();
    }

    // ExpiredPassword: Se l'utente arriva dal processo di cambio password per password scaduta
    if (this.expiredPassword()) {
      this.newPasswordMode = 'ExpiredPassword';
    }

    // StandardChange: Se l'utente arriva dal processo di cambio password standard
    if (!oobCode && !tenantId && !uid && !this.forgotPassword() && !this.expiredPassword()) {
      this.newPasswordMode = 'StandardChange';
    }
  }

  saveNewPassword() {
    if (this.passwordForm.valid) {
      switch (this.newPasswordMode) {
        case 'FirstTime':
          this.setFirstPassword(this.oobCode()!, this.tenantId()!, this.passwordForm.get('password')?.getRawValue());
          break;
        case 'ExpiredPassword':
          this.renewPassword(this.passwordForm.get('oldPassword')?.getRawValue(), this.passwordForm.get('password')?.getRawValue())
          break;
        case 'OTPReset':
          this.finalizeResetPassword(this.passwordForm.get('password')?.getRawValue());
          break;
        case 'StandardChange':
          this.changePassword(this.passwordForm.get('oldPassword')?.getRawValue(), this.passwordForm.get('password')?.getRawValue());
          break;
      }
    }
  }

  // Con oobCode e tenantId, l'utente deve inserire solo la nuova password
  setFirstPassword(oobCode: string, tenantId: string, newPassword: string) {
    this.loading = true;
    this.authSrv.resetPassword(oobCode, tenantId, newPassword)
      .pipe(finalize(() => this.loading = false))
      .subscribe(
        {
          next: (res) => {
            console.debug('Password Changed!');
            this.toastSrv.show({ type: 'success', message: 'Password aggiornata con successo' });
            this.router.navigate(['/auth/login']);
          },
          error: (err) => {
            this.router.navigate(['/auth/resend-oobcode']);
          }
        });
  }

  // Con Bearer Token, l'utente deve inserire la vecchia password
  changePassword(oldPassword: string, newPassword: string) {
    this.loading = true;

    this.authSrv.changePassword(oldPassword, newPassword)
      .pipe(finalize(() => this.loading = false))
      .subscribe(
        {
          next: (res) => {
            console.debug('Password Changed!');
            this.toastSrv.show({ type: 'success', message: 'Password aggiornata con successo' });
            this.router.navigate(['/auth/login']);
          }
        })
  }

  // con OTP, l'utente deve inserire la vecchia password
  renewPassword(oldPassword: string, newPassword: string) {
    this.loading = true;

    this.authSrv.renewPassword(oldPassword, newPassword)
      .pipe(finalize(() => this.loading = false))
      .subscribe(
        {
          next: (res) => {
            console.debug('Password Changed!');
            this.toastSrv.show({ type: 'success', message: 'Password aggiornata con successo' });
            this.passwordChanged.emit();
          }
        })
  }

  // Con otpCode e sessionId, l'utente deve inserire la nuova password
  finalizeResetPassword(newPassword: string) {
    this.loading = true;

    this.authSrv.finalizePasswordReset(newPassword)
      .pipe(finalize(() => this.loading = false))
      .subscribe(
        {
          next: (res) => {
            console.debug('Password Changed!');
            this.toastSrv.show({ type: 'success', message: 'Password aggiornata con successo' });
            this.router.navigate(['/auth/login']);
          }
        })
  }

  returnToOTP() {
    this.resetOTP.emit();
  }

  resendOobCode() {
    const oobCode = this.oobCode();
    const tenantId = this.tenantId();
    const uid = this.uid();

    if (oobCode && tenantId && uid) {
      this.resendOobCodeEmail(tenantId, uid);
    }
  }

  private verifyOobCode(oobCode: string, tenantId: string, uid: string) {
    this.loadingOobVerification = true;
    this.authSrv.verifyOobCode(oobCode, tenantId, uid)
      .pipe(finalize(() => this.loadingOobVerification = false))
      .subscribe(
        {
          next: (res) => {
            this.oobCodeErrorMsg = '';
          },
          error: (err) => {
            this.oobCodeNotValid = 'Deprecated';
            this.oobCodeErrorMsg = err.error.detail || 'Il link per impostare la password non è più valido.';
          }
        });
  }

  private resendOobCodeEmail(tenantId: string, uid: string) {
    this.loadingOobResend = true;
    this.authSrv.resendOobCodeEmail(tenantId, uid)
      .pipe(finalize(() => this.loadingOobResend = false))
      .subscribe(
        {
          next: (res) => {
            this.oobCodeErrorMsg = 'Inviato nuovamente il link per impostare la password. Controlla la tua casella di posta.';
            this.oobCodeNotValid = 'Resended';
          },
          error: (err) => {
            this.oobCodeErrorMsg = err.error.detail || 'Errore nell\'invio del link di primo accesso.';
          }
        });
  }

  private removeOldPasswordControl() {
    this.passwordForm.controls['oldPassword'].removeValidators(Validators.required);
    this.requiredOldPassword = false;
  }

  get password() { return this.passwordForm.get('password'); }
  get confirmPassword() { return this.passwordForm.get('confirmPassword'); }
  get oldPassword() { return this.passwordForm.get('oldPassword'); }
}
